import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import { Card, Table, Input, Button, Select, Typography, Tag } from "antd";
import { PlusOutlined, SearchOutlined, EditOutlined } from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { useCommon } from "../../../context/CommonContext";

const { Option } = Select;
const { Title, Text } = Typography;
const { Search } = Input;

const SubGenreList = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const notify = useNotify();

  // State management
  const [loading, setLoading] = useState(false);
  const [subGenres, setSubGenres] = useState([]);
  const [genres, setGenres] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) =>
      `${range[0]}-${range[1]} of ${total} sub genres`,
  });

  // Filters
  const [filters, setFilters] = useState({
    searchText: "",
    status: "all",
  });

  // Typing fired one request per keystroke. The term is now searched once the
  // typing settles, on the same 300ms the users and promos lists use.
  const searchDebounceRef = useRef(null);
  const SEARCH_DEBOUNCE_MS = 300;
  // Monotonic request id — responses from superseded requests are dropped so a
  // slow earlier search can't overwrite the results of the latest one.
  const requestSeqRef = useRef(0);

  const getSubGenres = useCallback(
    async (params = {}) => {
      const requestId = ++requestSeqRef.current;
      try {
        setLoading(true);

        const requestParams = {
          perPage: pagination.pageSize,
          currentPage: pagination.current,
          searchText: filters.searchText,
          genre_id: filters.genre_id,
          ...params,
        };

        console.log("Fetching genres with params:", requestParams);
        const response = await masterService.getSubGenres(requestParams);
        if (requestId !== requestSeqRef.current) return; // superseded by a newer request
        console.log("Genres API Response:", response);

        if (response.data && response.data.status) {
          // Handle different possible response structures
          const genresData =
            response.data.data?.data || response.data.data || [];
          const paginationData =
            response.data.data?.pagination || response.data.pagination || {};

          console.log("Genres data:", genresData);
          console.log("Pagination data:", paginationData);

          // Ensure genresData is an array
          if (Array.isArray(genresData)) {
            setSubGenres(genresData);
          } else {
            console.error("Genres data is not an array:", genresData);
            setSubGenres([]);
          }

          setPagination((prev) => ({
            ...prev,
            ...paginationData,
            current: paginationData.currentPage || 1,
          }));
        } else {
          notify.error({ message: "Failed to fetch genres" });
          setSubGenres([]);
        }
      } catch (error) {
        if (requestId !== requestSeqRef.current) return;
        console.error("Error fetching genres:", error);
        notify.error({ message: "Failed to fetch genres" });
        setSubGenres([]);
      } finally {
        // Only the newest request clears the spinner — an overtaken one finishing
        // early would otherwise show the list as loaded while it is still loading.
        if (requestId === requestSeqRef.current) setLoading(false);
      }
    },
    [filters.searchText, filters.status]
  );

  const loadGenres = async () => {
    try {
      const res = await masterService.getGenres({ isAll: true });
      console.log("res123", res);
      const list = res?.data?.data?.data || res?.data?.data || res?.data || [];
      setGenres(list);
    } catch (e) {
      notify.error({ message: "Failed to load genres" });
    }
  };

  // Load genres data. Mount only: every later fetch is an explicit
  // getSubGenres(...) from a search, status, parent-genre, or pagination handler.
  // Re-running this whenever getSubGenres is rebuilt would fire a second,
  // redundant request alongside each of them — and one per keystroke, which is
  // what the debounce below exists to stop.
  useEffect(() => {
    setPageTitle("Sub Genres");
    getSubGenres();
    loadGenres();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value ?? "";
    setFilters((prev) => ({ ...prev, searchText: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      getSubGenres({
        searchText: value.trim(),
        perPage: pagination.pageSize,
        currentPage: 1,
      });
      searchDebounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
  };

  // Enter key / search icon applies immediately, cancelling any pending debounce
  // so the term is not searched twice.
  const handleSearchNow = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    getSubGenres({
      searchText: (filters.searchText || "").trim(),
      perPage: pagination.pageSize,
      currentPage: 1,
    });
  };

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  // Handle status filter change
  const handleStatusChange = (value) => {
    setFilters((prev) => ({ ...prev, status: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    getSubGenres({
      searchText: filters.searchText,
      status: value,
      perPage: pagination.pageSize,
      currentPage: 1,
    });
  };

  const handleParentGenreChange = (value) => {
    setFilters((prev) => ({ ...prev, genre_id: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    getSubGenres({
      searchText: filters.searchText,
      genre_id: value,
      perPage: pagination.pageSize,
      currentPage: 1,

    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;

    setPagination((prev) => ({
      ...prev,
      current,
      pageSize,
    }));

    getSubGenres({
      searchText: filters.searchText,
      status: filters.status,
      perPage: pageSize,
      currentPage: current,
    });
  };

  // Handle add genre
  const handleAddGenre = () => {
    navigate("/sub-genres/add/new");
  };

  // Handle edit genre
  const handleEditGenre = (genreId) => {
    navigate(`/sub-genres/edit/${genreId}`);
  };

  // Table columns
  const columns = [
    {
      title: "Genre",
      dataIndex: "genre_name",
      key: "genre_name",
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.genre_name.localeCompare(b.genre_name),
      render: (text) => (
        <Text strong style={{ fontSize: "14px" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Parent",
      dataIndex: "parent_genre_name",
      key: "parent_genre_name",
      render: (parent_genre_name) => (
        <Text strong style={{ fontSize: "14px" }}>
          {parent_genre_name}
        </Text>
      ),
    },
    {
      title: "Show in Preference",
      dataIndex: "show_in_preference",
      key: "show_in_preference",

      render: (status) => (
        <Tag color={status === "Y" ? "green" : "red"}>
          {status === "Y" ? "Yes" : "No"}
        </Tag>
      ),
    },
    {
      title: "Preference Order Number",
      dataIndex: "preference_order_number",
      key: "preference_order_number",

      render: (status) => (
        <Text strong style={{ fontSize: "14px" }}>
          {status}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "genre_is_active",
      key: "genre_is_active",
      width: 120,
      render: (status) => (
        <Tag color={status === "Y" ? "green" : "red"}>
          {status === "Y" ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditGenre(record.genre_id)}
          size="small"
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Card>
      {/* Header: title + primary action */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Video Sub Genres ({pagination.total})
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddGenre}
          size="middle"
        >
          Add Video Sub Genre
        </Button>
      </div>

      {/* Filters row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "16px",
          flexWrap: "wrap",
        }}
      >
        <Search
          placeholder="Search sub genres..."
          allowClear
          value={filters.searchText}
          onChange={handleSearchChange}
          onSearch={handleSearchNow}
          style={{ width: 280 }}
          enterButton={<SearchOutlined />}
        />
        <Select
          value={filters.status}
          onChange={handleStatusChange}
          placeholder="Status"
          style={{ width: 140 }}
        >
          <Option value="all">All</Option>
          <Option value="Y">Active</Option>
          <Option value="N">Inactive</Option>
        </Select>
        <Select
          value={filters.genre_id}
          onChange={handleParentGenreChange}
          placeholder="Parent genre"
          style={{ width: 160 }}
          allowClear
        >
          <Option value="all">All</Option>
          {genres.map((genre) => (
            <Option key={genre.genre_id} value={genre.genre_id}>
              {genre.genre_name}
            </Option>
          ))}
        </Select>
      </div>

      <Table
        columns={columns}
        dataSource={subGenres}
        rowKey="genre_id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 800 }}
        size="middle"
        locale={{
          emptyText: "No sub genres found",
          filterConfirm: "OK",
          filterReset: "Reset",
          filterEmptyText: "No filters",
        }}
      />
    </Card>
  );
};

export default SubGenreList;
